import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Space,
  Button,
  Typography,
  Alert,
  Select,
  Checkbox,
  Table,
  Tag,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { AppBarCMS } from '../../../../basic/index.jsx';
import { CollapseBlock, Spinner, QuestionModal } from '../../../../common/index.jsx';
import AcceptedStudentsImport from '../IngProjectsSettings/AcceptedStudentsImport.jsx';
import { useGetStudyProgramsForIngProjectsQuery } from '../../../../../redux/services/study_programs.jsx';
import {
  useGetAssignmentStatusQuery,
  useAutoAssignStudentsMutation,
  useUnassignStudentsMutation,
  usePublishAssignmentMutation,
} from '../../../../../redux/services/ing_projects_assignments.jsx';
import {
  useImportAcceptedStudentsMutation,
} from '../../../../../redux/services/ing_projects_settings.jsx';
import { ingProjectsApi } from '../../../../../redux/services/ing_projects.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import {
  ROLE_ING_PROJECTS_ADMIN,
  ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR,
  ROLE_STUDY_PROGRAM_GUARANTEE,
} from '../../../../../utils/constants/roles.js';
import AssignmentMatrix from './AssignmentMatrix.jsx';

const IngProjectsStudentAssignmentPage = () => {
  const { t } = useTranslation();

  const { successMessage, errorMessage } = useToastMessages();

  const { data: studyPrograms, isLoading: isLoadingPrograms } =
    useGetStudyProgramsForIngProjectsQuery();
  const {
    data: statusData,
    isLoading: isLoadingStatus,
    refetch: refetchAssignmentStatus,
  } = useGetAssignmentStatusQuery(undefined, { refetchOnMountOrArgChange: true });

  const [importOpen, setImportOpen] = useState(false);
  const [importAcceptedStudents, { error: importError, reset: resetImport }] =
    useImportAcceptedStudentsMutation();

  const [autoAssign, { isLoading: isAssigning }] =
    useAutoAssignStudentsMutation();
  const [unassign, { isLoading: isUnassigning }] =
    useUnassignStudentsMutation();
  const [publishAssignment, { isLoading: isPublishing }] =
    usePublishAssignmentMutation();
  const dispatch = useDispatch();

  const isAdmin = useUserHasRole([ROLE_ING_PROJECTS_ADMIN]);
  const isStudyProgramGuarantor = useUserHasRole([ROLE_STUDY_PROGRAM_GUARANTEE]);
  const isAssignmentCoordinator = useUserHasRole([ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR]);

  // Guarantor and coordinator see only the study programs they manage
  // (guaranteed or coordinated), which the backend returns in the assignment status.
  const visibleStudyPrograms = useMemo(() => {
    if (!Array.isArray(studyPrograms)) {
      return [];
    }

    if (isAdmin) {
      return studyPrograms;
    }

    const managedProgramIds = new Set(
      (statusData?.data || []).map((program) => program.id),
    );

    return studyPrograms.filter((sp) => managedProgramIds.has(sp.id));
  }, [studyPrograms, statusData, isAdmin]);

  const canSelectPrograms = isAdmin || visibleStudyPrograms.length > 1;

  const isSingleProgram =
    Array.isArray(visibleStudyPrograms) && visibleStudyPrograms.length === 1;

  const getPublishedStatus = (programId) => {
    return (
      statusData?.data?.find((s) => s.id === programId)?.is_published || false
    );
  };

  const [matrixProgramId, setMatrixProgramId] = useState(null);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [studentFilter, setStudentFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [notifyStudents, setNotifyStudents] = useState(true);
  const [showUnassignConfirmModal, setShowUnassignConfirmModal] = useState(false);
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);

  const isAnySelectedPublished = useMemo(() => {
    return selectedProgramIds.some((id) => getPublishedStatus(id));
  }, [statusData, selectedProgramIds]);

  const areAllSelectedPublished = useMemo(() => {
    return (
      selectedProgramIds.length > 0 &&
      selectedProgramIds.every((id) => getPublishedStatus(id))
    );
  }, [statusData, selectedProgramIds]);

  const selectedAcceptedStudentsCount = useMemo(() => {
    return selectedProgramIds.reduce((total, programId) => {
      const program = statusData?.data?.find((item) => item.id === programId);
      return total + (program?.accepted_students_count || 0);
    }, 0);
  }, [selectedProgramIds, statusData]);

  const hasSelectedAcceptedStudents = selectedAcceptedStudentsCount > 0;

  const totalAcceptedStudentsCount = useMemo(() => {
    return (statusData?.data || []).reduce(
      (total, program) => total + (program.accepted_students_count || 0),
      0,
    );
  }, [statusData]);

  const hasAnyAcceptedStudents = totalAcceptedStudentsCount > 0;
  const visibleProgramIds = visibleStudyPrograms.map((program) => program.id);
  const areAllVisibleProgramsPublished = visibleProgramIds.length > 0
    && visibleProgramIds.every((programId) => getPublishedStatus(programId));
  const canHidePublishReminder =
    isAdmin || isStudyProgramGuarantor || isAssignmentCoordinator;
  const showPublishReminderAlert = !(
    canHidePublishReminder && areAllVisibleProgramsPublished
  );

  const title = t('Priradenie študentov');

  const handleImportSubmit = async (values) => {
    try {
      const response = await importAcceptedStudents({ file: values.file }).unwrap();

      setImportOpen(false);
      resetImport();
      await refetchAssignmentStatus();

      successMessage({
        message: t('Import úspešný'),
        description: response?.count
          ? t('Importovaných študentov: ') + response.count
          : undefined,
      });
    } catch {
      errorMessage({
        message: t('Import neúspešný'),
        description: t('V importovanom súbore je chyba.'),
      });
    }
  };

  useEffect(() => {
    document.title = title + ' | FRI UNIZA';
  }, [title]);

  const programOptions = useMemo(
    () =>
      Array.isArray(visibleStudyPrograms)
        ? visibleStudyPrograms
            .map((sp) => ({
              value: sp.id,
              label: sp.name_sk || sp.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'sk'))
        : [],
    [visibleStudyPrograms],
  );

  useEffect(() => {
    if (isSingleProgram) {
      setSelectedProgramIds([visibleStudyPrograms[0].id]);
      setMatrixProgramId(visibleStudyPrograms[0].id);
    }
  }, [visibleStudyPrograms, isSingleProgram]);

  useEffect(() => {
    if (!canSelectPrograms) {
      const allowedProgramIds = new Set(
        visibleStudyPrograms.map((program) => program.id),
      );

      setSelectedProgramIds((current) =>
        current.filter((programId) => allowedProgramIds.has(programId)),
      );

      if (matrixProgramId && !allowedProgramIds.has(matrixProgramId)) {
        setMatrixProgramId(visibleStudyPrograms[0]?.id ?? null);
      }
    }
  }, [visibleStudyPrograms, canSelectPrograms, matrixProgramId]);

  const handleAssign = async () => {
    if (selectedProgramIds.length === 0) {
      errorMessage({ message: t('Vyberte aspoň jeden študijný program') });
      return;
    }

    try {
      let totalAssigned = 0;
      for (const programId of selectedProgramIds) {
        const result = await autoAssign({ studyProgramId: programId }).unwrap();
        totalAssigned += result?.data?.assigned ?? result?.assigned ?? 0;
      }
      successMessage({
        message: t('Priradenie úspešné'),
        description: t('Priradených študentov: ') + totalAssigned,
      });
    } catch (error) {
      errorMessage({
        message: t('Priradenie neúspešné'),
        description:
          error?.data?.message || t('Nastala chyba pri priraďovaní.'),
      });
    }
  };

  const handleUnassign = async () => {
    if (selectedProgramIds.length === 0) {
      errorMessage({ message: t('Vyberte aspoň jeden študijný program') });
      return;
    }

    try {
      let totalRemoved = 0;
      for (const programId of selectedProgramIds) {
        const result = await unassign({ studyProgramId: programId }).unwrap();
        totalRemoved += result?.data?.removed ?? result?.removed ?? 0;
      }
      successMessage({
        message: t('Zrušenie priradenia úspešné'),
        description: t('Zrušených priradení: ') + totalRemoved,
      });
    } catch (error) {
      errorMessage({
        message: t('Zrušenie priradenia neúspešné'),
        description:
          error?.data?.message || t('Nastala chyba pri rušení priradenia.'),
      });
    }
  };

  const handlePublish = async () => {
    if (selectedProgramIds.length === 0) {
      errorMessage({ message: t('Vyberte aspoň jeden študijný program') });
      return;
    }

    try {
      for (const programId of selectedProgramIds) {
        await publishAssignment({
          studyProgramId: programId,
          notifyStudents,
        }).unwrap();
      }
      dispatch(
        ingProjectsApi.util.invalidateTags([
          'IngProjectsAssigned',
          'IngProjectsStudentStatus',
        ]),
      );
      successMessage({ message: t('Priradenie bolo zverejnené') });
    } catch (error) {
      errorMessage({
        message: t('Zverejnenie neúspešné'),
        description:
          error?.data?.message || t('Nastala chyba pri zverejňovaní.'),
      });
    }
  };

  if (isLoadingPrograms) {
    return <Spinner />;
  }

  return (
    <>
      {importOpen && (
        <AcceptedStudentsImport
          toggle={() => {
            setImportOpen(false);
            resetImport();
          }}
          validationErrors={
            Array.isArray(importError?.data?.errors)
              ? importError.data.errors
              : []
          }
          onSubmit={handleImportSubmit}
        />
      )}

      <AppBarCMS
        title={title}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        mainActions={
          isAdmin
            ? [
                {
                  onClick: () => setImportOpen(true),
                  title: t('Importovať prijatých študentov'),
                  icon: <UploadOutlined />,
                },
              ]
            : []
        }
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock
          title={t('Priradenie študentov na projekty')}
          collapsible={true}
        >
          {!hasAnyAcceptedStudents && !isLoadingStatus && (
            <Alert
              type="warning"
              showIcon
              title={t('Prijatí študenti zatiaľ neboli importovaní')}
              description={
                <>
                  <div>
                    {t(
                      'Na pokračovanie je potrebné najskôr naimportovať prijatých študentov. Kontaktujte administrátora modulu inž. projektov.',
                    )}
                  </div>
                </>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          <Alert
            type="info"
            showIcon
            title={t('Algoritmus automatického priraďovania')}
            description={
              <>
                {t(
                  'Tento algoritmus automaticky priradí študentov na projekty vybraného študijného programu. ',
                )}
                {t('Najskôr priradí študentov, ktorí majú prioritu garanta projektu, ')}
                {t(
                  'potom priradí študentov na projekty podľa nimi zadanej priority s tým, ',
                )}
                {t(
                  'že študenti s lepším priemerom majú v priraďovacom algoritme prednosť. ',
                )}
                {t(
                  'Po skončení zostávajú nepriradení len tí študenti, ktorých všetky vybrané projekty sú už plné, alebo nemali v systéme vybraný žiadny projekt. ',
                )}
                {t(
                  'Algoritmus priraďuje len nepriradených študentov. Ak bol študent priradený na projekt, algoritmus mu projekt nezmení.',
                )}
              </>
            }
          />
          {!isLoadingStatus && statusData?.data?.length > 0 && (
            <Table
              dataSource={statusData.data}
              rowKey="id"
              pagination={false}
              size="small"
              style={{ marginBottom: 16, marginTop: 16 }}
              rowSelection={
                canSelectPrograms
                  ? {
                      selectedRowKeys: selectedProgramIds,
                      onChange: (selectedRowKeys) => setSelectedProgramIds(selectedRowKeys),
                      getCheckboxProps: (row) => ({
                        disabled: !(row.accepted_students_count > 0),
                      }),
                    }
                  : undefined
              }
              columns={[
                {
                  title: t('Študijný program'),
                  dataIndex: 'name',
                },
                {
                  title: t('Počet priradených/prijatých'),
                  dataIndex: 'accepted_students_count',
                  align: 'center',
                  render: (_, row) =>
                    `${row.assigned_students_count || 0}/${row.accepted_students_count || 0}`,
                },
                {
                  title: t('Priradenie publikované'),
                  dataIndex: 'is_published',
                  align: 'center',
                  render: (value) => (
                    <Tag color={value ? 'green' : 'red'}>
                      {value ? t('Áno') : t('Nie')}
                    </Tag>
                  ),
                },
              ]}
            />
          )}

          {showPublishReminderAlert && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              title={t('Po ukončení priraďovania nezabudnite na zverejnenie')}
              description={t(
                'Po zverejnení priradenia sa odošlú e-maily garantom projektov a študentom s informáciou o priradení na projekty.',
              )}
            />
          )}

          <Space wrap align="center">
            <Button
              type="primary"
              onClick={handleAssign}
              loading={isAssigning}
              disabled={
                isAssigning ||
                isAnySelectedPublished ||
                !hasSelectedAcceptedStudents
              }
            >
              {t('Priradiť študentov')}
            </Button>
            <Button
              danger
              loading={isUnassigning}
              disabled={
                isUnassigning ||
                isAnySelectedPublished ||
                !hasSelectedAcceptedStudents
              }
              onClick={() => setShowUnassignConfirmModal(true)}
            >
              {t('Zrušiť priradenie')}
            </Button>
            <Button
              type="primary"
              loading={isPublishing}
              disabled={
                isPublishing ||
                areAllSelectedPublished ||
                !hasSelectedAcceptedStudents
              }
              onClick={() => setShowPublishConfirmModal(true)}
            >
              {t('Zverejniť priradenie')}
            </Button>
          </Space>
        </CollapseBlock>

        <CollapseBlock title={t('Matica priradení')} collapsible={true}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            {canSelectPrograms ? (
              <Select
                style={{ width: 300 }}
                placeholder={t('Vyberte študijný program')}
                options={programOptions}
                value={matrixProgramId}
                onChange={(value) => setMatrixProgramId(value)}
                allowClear
              />
            ) : (
              <Typography.Text>
                <strong>Študijný program</strong>: {programOptions[0]?.label}
              </Typography.Text>
            )}
            <Select
              style={{ width: 280 }}
              value={studentFilter}
              onChange={setStudentFilter}
              options={[
                { value: 'all', label: t('Všetci študenti') },
                { value: 'assigned', label: t('Priradení študenti') },
                { value: 'unassigned', label: t('Nepriradení študenti') },
              ]}
            />
            <Select
              style={{ width: 280 }}
              value={projectFilter}
              onChange={setProjectFilter}
              options={[
                { value: 'all', label: t('Všetky projekty') },
                { value: 'occupied', label: t('Obsadené projekty') },
                { value: 'empty', label: t('Neobsadené projekty') },
              ]}
            />
          </div>
        </CollapseBlock>
        {matrixProgramId && (
          <AssignmentMatrix
            studyProgramId={matrixProgramId}
            studentFilter={studentFilter}
            projectFilter={projectFilter}
            isPublished={getPublishedStatus(matrixProgramId)}
          />
        )}
      </div>
      <QuestionModal
        visible={showUnassignConfirmModal}
        toggle={() => setShowUnassignConfirmModal(false)}
        headerTitle={t('Zrušiť priradenie')}
        question={t('Naozaj chcete zrušiť priradenie študentov pre vybrané programy?')}
        yes={t('Áno')}
        yesDanger
        yesLoading={isUnassigning}
        chosenPositive={handleUnassign}
      />
      <QuestionModal
        visible={showPublishConfirmModal}
        toggle={() => setShowPublishConfirmModal(false)}
        headerTitle={t('Zverejniť priradenie')}
        question={(
          <div>
            <p>{t('Naozaj chcete zverejniť priradenie? Garanti projektov a študenti budú informovaní e-mailom o priradení na projekty.')}</p>
            <Checkbox
              checked={notifyStudents}
              disabled={!hasSelectedAcceptedStudents}
              onChange={(e) => setNotifyStudents(e.target.checked)}
            >
              {t('Odoslať oznámenie študentom')}
            </Checkbox>
          </div>
        )}
        yes={t('Áno')}
        yesLoading={isPublishing}
        chosenPositive={handlePublish}
      />
    </>
  );
};

export default IngProjectsStudentAssignmentPage;
